import { AgentAvatar } from "@/components/molecules/agent-avatar";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import type { OnboardingDraft } from "@/features/onboarding/types";
import { useTranslation } from "@/features/utils/i18n";
import {
  hasTemperamentSample,
  useSamplePlayer,
} from "@/features/voices/hooks/use-sample-player";
import {
  useVoiceProviders,
  useVoices,
} from "@/features/voices/hooks/use-voices";
import { PauseIcon, PlayIcon } from "@phosphor-icons/react";
import type { Voice } from "@/features/voices/types";
import { OnboardingLayout } from "./layout";

/**
 * The faces on offer.
 *
 * A handful rather than all twenty: this is a first impression, not a casting
 * call, and the agent's avatar is editable afterwards. Alternating genders so
 * the row reads as a choice of person rather than two blocks to compare.
 */
const CHOICES: { avatarId: string; gender: "female" | "male" }[] = [
  { avatarId: "f01", gender: "female" },
  { avatarId: "m02", gender: "male" },
  { avatarId: "f05", gender: "female" },
  { avatarId: "m05", gender: "male" },
  { avatarId: "f08", gender: "female" },
  { avatarId: "m09", gender: "male" },
];

/**
 * Voices are ElevenLabs unless this deployment has none.
 *
 * Six faces need six voices, and mixing providers to fill the row would mean
 * the client auditions two different levels of quality and picks the person,
 * not the synthesis. ElevenLabs is the one we ship the full catalogue for, so
 * it is the pool; anything else is a fallback for a deployment without it.
 */
const PREFERRED_PROVIDER = "elevenlabs";

/**
 * One voice per face, as far as the catalogue allows.
 *
 * Faces are numbered within their own gender, and the voice pool is indexed by
 * that number — so each portrait keeps its own voice, stably, and the client
 * hears six people rather than two. When the pool is shorter than the row it
 * wraps, which is the honest degradation: a repeated voice beats a silent tile.
 */
const voiceForFace = (
  pool: Voice[],
  avatarId: string,
  gender: "female" | "male",
): Voice | undefined => {
  const matching = pool.filter(
    (voice) => voice.gender?.toLowerCase() === gender,
  );
  if (matching.length === 0) return undefined;

  const rank = CHOICES.filter((choice) => choice.gender === gender).findIndex(
    (choice) => choice.avatarId === avatarId,
  );
  return matching[(rank < 0 ? 0 : rank) % matching.length];
};

/**
 * Who the client is hiring.
 *
 * Two questions, and both are about the person rather than the configuration:
 * a face and a name. Picking a face is how the voice gets chosen — the avatars
 * are already animated portraits, so a row of them says "choose someone" in a
 * way a "male voice / female voice" pair of buttons never did.
 *
 * The greeting and the room tone used to be here and are gone. The greeting
 * cannot be written well at this point anyway: the company name is still being
 * inferred, so it is composed server-side at the end, once both names exist.
 */
export function StepAgent({
  draft,
  onChange,
  onNext,
  onBack,
  isSubmitting,
  analysisPending,
}: {
  draft: OnboardingDraft;
  onChange: (patch: OnboardingDraft) => void;
  onNext: () => void;
  onBack: () => void;
  isSubmitting?: boolean;
  analysisPending?: boolean;
}) {
  const { t } = useTranslation("onboarding");

  const { data: voices = [] } = useVoices();
  const { data: usableProviders = [] } = useVoiceProviders();

  const agent = draft.agent || {};
  const setAgent = (patch: Partial<NonNullable<OnboardingDraft["agent"]>>) =>
    onChange({ agent: { ...agent, ...patch } });

  const selected = agent.avatar_id || CHOICES[0].avatarId;

  const selectableVoices =
    usableProviders.length > 0
      ? voices.filter((voice) => usableProviders.includes(voice.provider_name))
      : voices;

  // The pool the faces draw from: this deployment's usable voices, in the
  // business's language, from the preferred provider when it has any.
  const language = draft.company?.default_language || "fr";
  const inLanguage = selectableVoices.filter((voice) =>
    voice.language?.toLowerCase().startsWith(language.toLowerCase()),
  );
  const preferred = inLanguage.filter(
    (voice) => voice.provider_name === PREFERRED_PROVIDER,
  );
  const pool = preferred.length > 0 ? preferred : inLanguage;

  const selectedVoice =
    voiceForFace(pool, selected, agent.voice_gender || "female") ||
    selectableVoices.find((voice) => voice.id === agent.voice);

  const pick = (avatarId: string, gender: "female" | "male") =>
    setAgent({
      avatar_id: avatarId,
      voice_gender: gender,
      // Send the voice they just heard, not just its gender: the preview is a
      // promise, and the server only picks for itself when this is empty.
      voice: voiceForFace(pool, avatarId, gender)?.id || agent.voice,
    });

  // The voice as it will really sound (the expressive preview, generated on first request),
  // falling back to its stored sample when there is no preview to get.
  const sample = useSamplePlayer();
  const isPlaying = !!selectedVoice && sample.playingId === selectedVoice.id;
  const canPlay =
    !!selectedVoice?.sample || hasTemperamentSample(selectedVoice);

  return (
    <OnboardingLayout
      step="agent"
      title={t("steps.agent.title")}
      // A reaction to the answer just given, rather than the same "OK" to all
      // four: the conversation is only a conversation if it listens.
      intro={t(
        `steps.agent.reactions.${draft.telephony?.install_type || "default"}`,
      )}
      onBack={onBack}
      onNext={onNext}
      isSubmitting={isSubmitting}
      analysisPending={analysisPending}
    >
      <div className="space-y-2">
        <div className="flex items-center justify-between">
          <Label>{t("steps.agent.voice")}</Label>
          <button
            type="button"
            onClick={() => selectedVoice && sample.toggle(selectedVoice)}
            disabled={!canPlay}
            className="flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground disabled:opacity-40"
          >
            {isPlaying ? (
              <PauseIcon className="h-3.5 w-3.5" weight="fill" />
            ) : (
              <PlayIcon className="h-3.5 w-3.5" weight="fill" />
            )}
            {t("steps.agent.playSample")}
          </button>
        </div>

        <div className="grid grid-cols-6 gap-2">
          {CHOICES.map(({ avatarId, gender }) => {
            const isSelected = selected === avatarId;
            return (
              <button
                key={avatarId}
                type="button"
                aria-pressed={isSelected}
                onClick={() => pick(avatarId, gender)}
                className={
                  "relative rounded-full p-1 transition-colors " +
                  (isSelected
                    ? "bg-primary/10 ring-2 ring-primary"
                    : "hover:bg-slate-500/5")
                }
              >
                <AgentAvatar
                  avatarId={avatarId}
                  name={agent.name}
                  size="lg"
                  className="h-full w-full"
                />
              </button>
            );
          })}
        </div>
      </div>

      <div className="space-y-2">
        <Label htmlFor="agent-name">{t("steps.agent.name")}</Label>
        <Input
          id="agent-name"
          value={agent.name || ""}
          onChange={(event) => setAgent({ name: event.target.value })}
          placeholder={t("steps.agent.namePlaceholder")}
        />
      </div>
    </OnboardingLayout>
  );
}
